import { useCallback } from 'react'

import { SalaryRetention, type TeamMemberStatistic } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import type { DialogProps } from '@/dialogs/types'
import { useSetCompensation } from '@/modules/accounting/hooks/useSetCompensation.ts'
import { getSalary } from '@/modules/accounting/utils.ts'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import { roundAmount } from '@/utils/number.ts'

import { MemberSalaryDialogFormFields } from './MemberSalaryDialogFormFields.tsx'
import { MemberSalarySchema, type MemberSalaryType } from './validations.ts'

interface Props extends DialogProps {
  formattedRange?: string
  data: TeamMemberStatistic
  showCents: boolean
}

export const SetMemberSalaryDialog = ({
  isOpen,
  onClose,
  formattedRange,
  data,
  showCents,
}: Props) => {
  const { updateRevenue, isLoading: isRevenueLoading } = useSetCompensation({
    type: 'member',
  })

  const form = useForm<MemberSalaryType>({
    defaultValues: {
      fixedSalary: {
        amount: data.compensation?.fixedSalary ?? 0,
        retention: data.compensation?.salaryRetention ?? SalaryRetention.YEARLY,
      },
      cutOfRevenuePercentage: data.compensation?.cutOfRevenuePercentage ?? 0,
    },
    resolver: zodResolver(MemberSalarySchema),
  })

  const onSubmit = useCallback(
    async (value: MemberSalaryType) => {
      await updateRevenue(data.memberId, value)
      onClose()
    },
    [data, onClose, updateRevenue],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      testId="set-salary-modal"
    >
      <Dialog.Header>
        <Dialog.Title>Set Member Revenue</Dialog.Title>
      </Dialog.Header>

      <div className="mt-4">
        <div>
          <div className="text-text-default mb-2 text-sm font-medium">
            Selected Member
          </div>
          <div className="border-secondary-100 bg-secondary-50 dark:bg-secondary-600 flex items-center rounded-md border px-3 py-2">
            <CreatorAvatar
              className="mr-2"
              name={data.username!}
              avatarSrc={data.profilePicture}
              size={24}
            />
            <div className="text-text-default">{data.username}</div>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <p className="text-text-default font-medium">Revenue</p>
          <p className="text-secondary-300 text-sm">({formattedRange})</p>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-text-default text-sm">Total Revenue</p>
          <p className="text-text-default text-sm font-medium">
            {roundAmount(data?.revenue, showCents)}
          </p>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-text-default text-sm">Member Salary</p>
          <p className="text-text-default text-sm font-medium">
            {roundAmount(getSalary(data), showCents)}
          </p>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-text-default text-sm">Revenue</p>
          <p className="text-text-default text-sm font-medium">
            {roundAmount(data.agencyProfit, showCents)}
          </p>
        </div>
      </div>

      <Separator className="my-5" />
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <MemberSalaryDialogFormFields disabled={isRevenueLoading} />
          <Dialog.Footer className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              type="button"
            >
              Cancel
            </Button>
            <Button
              isLoading={isRevenueLoading}
              type="submit"
              testId="set-salary-submit-button"
            >
              Save
            </Button>
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
